// hooks/usePermissions.ts
import { useCallback, useEffect, useState, useRef } from 'react';
import { doc, onSnapshot, Unsubscribe, collection, query, where, getDocs, getDoc } from 'firebase/firestore';
import { Permissions, TeamsStatus } from 'ts-interfaces';
import { permissionsCache, incrementListenerCount, decrementListenerCount } from './permissionsShared';

// Cache for team status to avoid repeated queries
const teamStatusCache = new Map<string, boolean>();
const teamStatusPromiseCache = new Map<string, Promise<boolean>>();

// Function to clear team status cache
export const clearTeamStatusCache = (uid?: string, designFirmID?: string) => {
    const key = uid && designFirmID ? `${uid}-${designFirmID}` : null;
    if (key) {
        teamStatusCache.delete(key);
        teamStatusPromiseCache.delete(key);
    } else if (uid) {
        Array.from(teamStatusCache.keys())
            .filter(k => k.startsWith(`${uid}-`))
            .forEach(k => { teamStatusCache.delete(k); teamStatusPromiseCache.delete(k); });
    } else {
        teamStatusCache.clear();
        teamStatusPromiseCache.clear();
    }
};

export const hasAccessToResource = ( permissions:Permissions | null, 
                resourceID:string, 
                accessType: 'read' | 'write',
                type: 'project' | 'board' | 'collection', 
                ownerUID:string, uid:string) => {

    if (!uid || !resourceID) return false; // No access if no owner or user ID
    if (ownerUID && (ownerUID === uid)) return true; // Owner has full access
    if (!permissions) return false; // No permissions defined    
    const writeKey = `${type}Write` as keyof Permissions;
    const hasWrite = (permissions[writeKey] as string[] | undefined)?.includes(resourceID) || false;
    if (hasWrite) return true; // Write access grants read access too
    
    if (accessType === 'read') {
        const readKey = `${type}Read` as keyof Permissions;
        return (permissions[readKey] as string[] | undefined)?.includes(resourceID) || false;
    }
    return false;
}


interface UsePermissionsReturn {
    permissions: Permissions | null;
    loading: boolean;
    error: Error | null;
    refetch: () => void;
    hasProjectAccess: (projectId: string, accessType?: 'read' | 'write', ownerId?: string) => boolean;
    hasBoardAccess: (boardId: string, accessType?: 'read' | 'write', ownerId?: string) => boolean;
    hasCollectionAccess: (collectionId: string, accessType?: 'read' | 'write', ownerId?: string) => boolean;
}

// Hook for individual user permissions with optional real-time updates
export const usePermissions = (
    uid: string | null | undefined, 
    db: any | null, 
    useSubscription: boolean = false
): UsePermissionsReturn => {
    const [permissions, setPermissions] = useState<Permissions | null>(null);
    const loadingRef = useRef(true);
    const [, forceUpdate] = useState({});
    const [error, setError] = useState<Error | null>(null);
    const unsubscribeRef = useRef<Unsubscribe | null>(null);
    const isInitialLoadRef = useRef(true);

    const setLoading = useCallback((value: boolean) => {
        if (loadingRef.current !== value) {
            loadingRef.current = value;
            forceUpdate({});
        }
    }, []);

    // Early return optimization for initial render
    useEffect(() => {
        if (!uid || !isInitialLoadRef.current) return;
        const cached = permissionsCache.get(uid);
        if (cached) { setPermissions(cached); setLoading(false); }
        isInitialLoadRef.current = false;
    }, [uid, setLoading]);

    const cleanupListener = useCallback(() => {
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
            if (uid) decrementListenerCount(uid);
        }
    }, [uid]);

    // Optimize checkTeamStatus to bail out early
    const checkTeamStatus = useCallback(async (permissionsData: Permissions): Promise<boolean> => {
        if (!permissionsData.teamsDesigner?.length || !db) return false;
        const designFirmID = permissionsData.teamsDesigner[0];
        const cacheKey = `${uid}-${designFirmID}`;
        
        const cached = teamStatusCache.get(cacheKey);
        if (cached !== undefined) return cached;
        
        let promise = teamStatusPromiseCache.get(cacheKey);
        if (promise) return promise;

        promise = (async () => {
            try {
                const q = query(collection(db, 'teams'),
                    where('designFirmID', '==', designFirmID),
                    where('uid', '==', uid));
                const snap = await getDocs(q);
                const isActive = snap.size > 0 && 
                    snap.docs[0].data().status !== TeamsStatus.INACTIVE;
                teamStatusCache.set(cacheKey, isActive);
                return isActive;
            } catch { return false; }
            finally { teamStatusPromiseCache.delete(cacheKey); }
        })();
        
        teamStatusPromiseCache.set(cacheKey, promise);
        return promise;
    }, [uid, db]);

    const processPermissions = useCallback(async (permissionsData: Permissions | null) => {
        const isActive = permissionsData && await checkTeamStatus(permissionsData);
        permissionsCache[isActive ? 'set' : 'delete'](uid!, isActive ? permissionsData : undefined);
        setPermissions(isActive ? permissionsData : null);
    }, [uid, checkTeamStatus]);

    const fetchPermissionsOnce = useCallback(async () => {
        if (!uid || !db) {
            setPermissions(null);
            setLoading(false);
            return;
        }
        setError(null); setLoading(true);
        try {
            const snap = await getDoc(doc(db, 'permissions', uid));
            await processPermissions(snap.exists() ? snap.data() as Permissions : null);
        } catch (e) {
            console.log('Dang yo! ' + e);
            setError(e instanceof Error ? e : new Error('Failed to fetch'));
            setPermissions(null);
        } finally { setLoading(false); }
    }, [uid, db, processPermissions, setLoading]);

    const setupPermissionListener = useCallback(async () => {
        if (!uid || !db) return setPermissions(null), setLoading(false);
        
        setError(null);
        cleanupListener();
        
        try {
            unsubscribeRef.current = onSnapshot(
                doc(db, 'permissions', uid),
                { source: 'default' },
                async (docSnap) => {
                    await processPermissions(docSnap.exists() ? docSnap.data() as Permissions : null);
                    setLoading(false);
                },
                (error) => {
                    console.error(`Error listening to permissions for ${uid}:`, error);
                    setError(error);
                    setLoading(false);
                }
            );
            incrementListenerCount(uid);
        } catch (e) {
            console.error('Error setting up permission listener:', e);
            setError(e instanceof Error ? e : new Error('Failed to setup permission listener'));
            setLoading(false);
        }
    }, [uid, db, cleanupListener, processPermissions]);

    useEffect(() => {
        useSubscription ? setupPermissionListener() : fetchPermissionsOnce();
        return () => useSubscription && cleanupListener();
    }, [useSubscription, setupPermissionListener, fetchPermissionsOnce]);

    const refetch = useCallback(() => 
        useSubscription ? setupPermissionListener() : fetchPermissionsOnce(), 
    [useSubscription, setupPermissionListener, fetchPermissionsOnce]);

    const createAccessChecker = (type: 'project' | 'board' | 'collection') => 
        useCallback((id: string, accessType: 'read' | 'write' = 'read', ownerId?: string): boolean => {
            return hasAccessToResource(permissions, id, accessType, type, ownerId || '', uid || '');          
            // if (accessType === 'write' && ownerId && uid === ownerId) return true;
            // if (!permissions) return false;
            // console.log('make it here?');
            // console.log('permissions', permissions);
            // const key = `${type}${accessType === 'write' ? 'Write' : 'Read'}` as keyof Permissions;
            // const bb =  (permissions[key] as string[] | undefined)?.includes(id) || false;
            // if (bb != z ) {
            //     console.warn(`Access check mismatch for ${type} ${id} (${accessType}): expected ${bb}, got ${z}`);
            // }
            // return bb
        }, [permissions, uid]);

    return {
        permissions,
        loading: loadingRef.current,
        error,
        refetch,
        hasProjectAccess: createAccessChecker('project'),
        hasBoardAccess: createAccessChecker('board'),
        hasCollectionAccess: createAccessChecker('collection')
    };
};